import {
  Avatar,
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';
import { usePrChartsGraphql } from '../../../../graphql/index';
import {
  NewDataGrid,
  Paginate,
  PeriodType,
} from '@devd-client/devd/components';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { IndividualCollaborationDetailItem } from '../../../../helpers/team-details.types';
import type { ColumnDef } from '@tanstack/react-table';

interface ShowPrSideDrawerProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  heading: string;
  subHeading: string;
  submitter: string;
  reviewer: string;
  teamId: string;
  selected: PeriodType;
}

const EXCLUDED_KEYS = [
  'summary',
  'link',
  'author',
  'addition',
  'deletions',
  'createdAt',
  'id',
];

const formatLabel = (label: string) => {
  return label
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str: string) => str.toUpperCase())
    .replace('Pr', 'PR');
};

const ShowPrSideDrawer: React.FC<ShowPrSideDrawerProps> = ({
  openDrawer,
  handleDrawerClose,
  heading,
  subHeading,
  submitter,
  reviewer,
  teamId,
  selected,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const { data: prData, isFetching: isChartPrFetching } = usePrChartsGraphql(
    selected.startDate,
    selected.endDate,
    selected?.sprintId,
    'PR_REVIEW',
    teamId,
    '',
    '',
    submitter,
    reviewer,
    currentPage,
    100,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const handlePageClick = useCallback(
    (page: number) => {
      setCurrentPage(page - 1);
    },
    [currentPage]
  );

  const hasCodeChanges = useMemo(
    () =>
      prData?.data?.some(
        (item: IndividualCollaborationDetailItem) =>
          (item?.addition ? parseInt(item.addition) : 0) !== 0 ||
          (item?.deletions ? parseInt(item.deletions) : 0) !== 0
      ),
    [prData?.data]
  );

  // Collect dynamic field keys from data
  const dynamicFieldKeys = useMemo(() => {
    const keysSet = new Set<string>();
    prData?.data?.forEach((item: IndividualCollaborationDetailItem) => {
      Object.entries(item).forEach(([key, value]) => {
        if (
          !EXCLUDED_KEYS.includes(key) &&
          value !== null &&
          value !== undefined
        ) {
          keysSet.add(key);
        }
      });
    });
    return Array.from(keysSet);
  }, [prData?.data]);

  const columns: ColumnDef<IndividualCollaborationDetailItem, any>[] = useMemo(
    () => [
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        enableSorting: false,
        size: 180,
        cell: ({ row }: any) => {
          const value = row.original?.createdAt;
          if (!value) return '-';
          return (
            <Tooltip hasArrow label={moment(value).format('llll')}>
              <Text
                fontFamily="heading"
                fontSize="sm"
                color="text.secondary"
                fontWeight="medium"
                noOfLines={1}
                whiteSpace="nowrap"
              >
                {moment(value).format('DD MMM YYYY, h:mm A')}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        header: 'Summary',
        accessorKey: 'summary',
        enableSorting: false,
        size: 320,
        cell: ({ row }: any) => {
          const { summary, id } = row.original || {};
          if (!summary) return '-';
          return (
            <Link to={`/timeline/pr/${id}`}>
              <Text
                fontSize="sm"
                fontFamily="heading"
                color="primary.500"
                fontWeight="medium"
                textDecoration="underline"
                noOfLines={2}
              >
                {summary}
              </Text>
            </Link>
          );
        },
      },
      {
        header: 'Author',
        accessorKey: 'author',
        enableSorting: false,
        size: 200,
        cell: ({ row }: any) => {
          const author = row.original?.author;
          if (!author) return '-';
          return (
            <Flex align="center">
              <Avatar size="xs" name={author} />
              <Text
                ml={2}
                fontSize="sm"
                fontFamily="heading"
                color="text.secondary"
                fontWeight="medium"
                noOfLines={1}
              >
                {author}
              </Text>
            </Flex>
          );
        },
      },
      ...dynamicFieldKeys.map(
        (key): ColumnDef<IndividualCollaborationDetailItem, any> => ({
          header: formatLabel(key),
          accessorKey: key,
          enableSorting: false,
          size: 160,
          cell: ({ row }: any) => {
            const value = row.original?.[key];
            if (value === null || value === undefined) return '-';

            if (key === 'updatedAt') {
              return (
                <Tooltip hasArrow label={moment(value).format('llll')}>
                  <Text
                    fontFamily="heading"
                    fontSize="sm"
                    color="text.secondary"
                    fontWeight="medium"
                    noOfLines={1}
                    whiteSpace="nowrap"
                  >
                    {moment(value).format('DD MMM YYYY, h:mm A')}
                  </Text>
                </Tooltip>
              );
            }

            return (
              <Text
                fontSize="sm"
                fontFamily="heading"
                color="text.secondary"
                fontWeight="medium"
                noOfLines={1}
              >
                {String(value)}
              </Text>
            );
          },
        })
      ),
      ...(hasCodeChanges
        ? [
            {
              header: 'Code Changes',
              accessorKey: 'codeChanges',
              enableSorting: false,
              size: 140,
              cell: ({ row }: any) => {
                const item = row.original;
                const additions = item?.addition ? parseInt(item.addition) : 0;
                const deletions = item?.deletions
                  ? parseInt(item.deletions)
                  : 0;

                if (!additions && !deletions) return '-';

                return (
                  <Flex gap={1}>
                    <Tooltip
                      hasArrow
                      placement="bottom-start"
                      label="Additions"
                    >
                      <Text
                        color="success"
                        fontFamily="heading"
                        fontSize="sm"
                        fontWeight="medium"
                      >
                        +{additions}
                      </Text>
                    </Tooltip>
                    <Tooltip hasArrow placement="bottom-end" label="Deletions">
                      <Text
                        color="error"
                        fontFamily="heading"
                        fontSize="sm"
                        fontWeight="medium"
                      >
                        -{deletions}
                      </Text>
                    </Tooltip>
                  </Flex>
                );
              },
            } as ColumnDef<IndividualCollaborationDetailItem, any>,
          ]
        : []),
    ],
    [dynamicFieldKeys, hasCodeChanges]
  );

  return (
    <Modal
      isOpen={openDrawer}
      onClose={handleDrawerClose}
      size="6xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader pb={2}>
          <Stack spacing={1}>
            <Text
              fontFamily="heading"
              fontSize="md"
              fontWeight="semibold"
              color="text.primary"
            >
              {heading}
            </Text>
            <Text
              fontFamily="heading"
              fontSize="sm"
              color="text.secondary"
              fontWeight="medium"
            >
              {subHeading}
            </Text>
          </Stack>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {isChartPrFetching && !prData?.data ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="400px"
                maxH="400px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  gap={4}
                >
                  <Spinner
                    thickness="3px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="primary.500"
                    size="lg"
                  />
                  <Text
                    fontFamily="heading"
                    fontSize="sm"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Loading data...
                  </Text>
                </Flex>
              </Box>
            ) : prData?.data && prData.data.length > 0 ? (
              <>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  overflow="hidden"
                  w="100%"
                >
                  <NewDataGrid
                    data={prData.data}
                    columns={columns}
                    showLoader={false}
                    skeletonLoader={false}
                    sticky="firstCol"
                    useMaxH
                    showGridLines
                  />
                </Box>

                {prData.data.length > 1 && (
                  <Paginate
                    currentPage={(prData?.currentPage ?? 0) + 1}
                    totalPageCount={prData?.totalPages ?? 1}
                    pageSize={20}
                    onPageChange={(page) => handlePageClick(page as number)}
                  />
                )}
              </>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ShowPrSideDrawer;
